import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, Upload } from 'lucide-react';
import { postApplicationSubmit, postDocumentFile, putApplication, type Application, type ApplicationDraft } from '@/api/applicant';
import { errMsg, fieldErrors } from '@/components/kit/data';
import { naira } from '@/lib/format';
import { appKeys, docLabel, newKey, useApplication } from '../shared';
import { Actions, Body, Field, Hero, Loading, Panel, Primary, Progress, Rows, Screen, Secondary, inputBig } from '../ui';

type Form = {
  admission_cycle_id: string; class_applied_id: string; first_name: string; middle_name: string; last_name: string; gender: string; date_of_birth: string;
  previous_school: string; religion: string; state_of_origin: string; nationality: string; blood_group: string; medical_notes: string;
  guardian_name: string; guardian_relationship: string; guardian_phone: string; guardian_email: string; address: string;
};
const EXTRA = ['middle_name', 'religion', 'state_of_origin', 'nationality', 'blood_group', 'medical_notes', 'guardian_relationship'] as const;
const empty: Form = { admission_cycle_id: '', class_applied_id: '', first_name: '', middle_name: '', last_name: '', gender: '', date_of_birth: '', previous_school: '', religion: '', state_of_origin: '', nationality: 'Nigerian', blood_group: '', medical_notes: '', guardian_name: '', guardian_relationship: '', guardian_phone: '', guardian_email: '', address: '' };

const fromApp = (a: Application): Form => {
  const x = (a.extra ?? {}) as Record<string, unknown>;
  return {
    admission_cycle_id: String(a.admission_cycle_id ?? ''), class_applied_id: a.class_applied_id ? String(a.class_applied_id) : '',
    first_name: a.first_name ?? '', last_name: a.last_name ?? '', gender: a.gender ?? '', date_of_birth: a.date_of_birth?.slice(0, 10) ?? '', previous_school: a.previous_school ?? '',
    guardian_name: a.guardian_name ?? '', guardian_phone: a.guardian_phone ?? '', guardian_email: a.guardian_email ?? '', address: a.address ?? '',
    ...(Object.fromEntries(EXTRA.map((k) => [k, String(x[k] ?? (k === 'nationality' ? 'Nigerian' : ''))])) as Pick<Form, typeof EXTRA[number]>),
  };
};

const STEPS = ['Child details', 'Parent or guardian', 'Documents', 'Review and submit'];
const NEED: Record<number, [keyof Form, string][]> = {
  0: [['first_name', 'First name'], ['last_name', 'Last name'], ['gender', 'Gender'], ['date_of_birth', 'Date of birth'], ['class_applied_id', 'Class']],
  1: [['guardian_name', 'Full name'], ['guardian_phone', 'Phone number'], ['address', 'Home address']],
};

export function ApplicationPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useApplication();
  const app = q.data?.application ?? null;
  const cycles = q.data?.cycles ?? [];
  const classes = q.data?.classes ?? [];
  const [form, setForm] = useState<Form>(empty);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current || q.isLoading) return;
    if (app) setForm(fromApp(app));
    else if (cycles.length) setForm((f) => ({ ...f, admission_cycle_id: String(cycles[0].id) }));
    seeded.current = true;
  }, [app, cycles, q.isLoading]);

  const refresh = () => { qc.invalidateQueries({ queryKey: appKeys.app }); qc.invalidateQueries({ queryKey: appKeys.status }); };
  const set = (k: keyof Form, v: string) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: '' })); };
  const cycle = cycles.find((c) => String(c.id) === form.admission_cycle_id) ?? app?.cycle ?? null;
  const editable = !app || app.editable;
  const required = app?.required_documents ?? cycle?.required_documents ?? [];

  const body = (): ApplicationDraft => ({
    ...(form.admission_cycle_id && (!app || app.status === 'draft') ? { admission_cycle_id: Number(form.admission_cycle_id) } : {}),
    class_applied_id: form.class_applied_id ? Number(form.class_applied_id) : null,
    first_name: form.first_name.trim(), last_name: form.last_name.trim(), gender: (form.gender || null) as ApplicationDraft['gender'], date_of_birth: form.date_of_birth || null,
    previous_school: form.previous_school.trim() || null, guardian_name: form.guardian_name.trim() || null, guardian_phone: form.guardian_phone.trim() || null,
    guardian_email: form.guardian_email.trim() || null, address: form.address.trim() || null,
    extra: { ...((app?.extra as Record<string, unknown>) ?? {}), ...Object.fromEntries(EXTRA.map((k) => [k, form[k].trim() || null])) },
  });

  const save = useMutation({ mutationFn: () => putApplication(body()), onSuccess: () => { setMsg(null); refresh(); }, onError: (e) => { setErrors(fieldErrors(e)); setMsg(errMsg(e)); } });
  const submit = useMutation({
    mutationFn: async () => { await putApplication(body()); return postApplicationSubmit(newKey()); },
    onSuccess: (a) => { refresh(); nav(a.status === 'payment_pending' ? '/applicant/payment' : '/applicant/overview'); },
    onError: (e) => setMsg(errMsg(e)),
  });

  const next = async () => {
    const miss = Object.fromEntries((NEED[step] ?? []).filter(([k]) => !String(form[k]).trim()).map(([k, l]) => [k, `${l} is required.`]));
    setErrors(miss);
    if (Object.keys(miss).length) { setMsg('Please fill the highlighted fields.'); return; }
    if (editable && step < 2) { try { await save.mutateAsync(); } catch { return; } }
    setMsg(null);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0 });
  };

  if (q.isLoading) return <Screen><Hero title="Application" /><Loading /></Screen>;
  if (!app && !cycles.length) return <Screen><Hero withSchool title="Applications are closed" /><Body><Panel><p className="text-slate-600">This school is not taking applications right now.</p></Panel></Body></Screen>;

  if (!editable && app) {
    return (
      <Screen>
        <Hero kicker={`Application ${app.reference}`} title="Your application" sub="Submitted. It can no longer be changed." />
        <Body><Panel><Summary form={form} classes={classes} app={app} /></Panel><Actions><Primary to="/applicant/overview">Track my application</Primary></Actions></Body>
      </Screen>
    );
  }

  const docsDone = required.filter((r) => r.required).every((r) => app?.documents.some((d) => d.type === r.type));

  return (
    <Screen>
      <Hero kicker={`Step ${step + 1} of ${STEPS.length}`} title={STEPS[step]} />
      <Body>
        <Progress step={step + 1} of={STEPS.length} />
        {msg && <Panel tone="red"><p className="text-sm text-red-700">{msg}</p></Panel>}

        {step === 0 && (
          <div className="space-y-4">
            {cycles.length > 1 && !app && (
              <Field label="Admission cycle"><select className={inputBig} value={form.admission_cycle_id} onChange={(e) => set('admission_cycle_id', e.target.value)}>{cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="First name" error={errors.first_name}><input className={inputBig} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} placeholder="Ada" /></Field>
              <Field label="Last name" error={errors.last_name}><input className={inputBig} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} placeholder="Okafor" /></Field>
            </div>
            <Field label="Middle name (optional)"><input className={inputBig} value={form.middle_name} onChange={(e) => set('middle_name', e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Gender" error={errors.gender}><select className={inputBig} value={form.gender} onChange={(e) => set('gender', e.target.value)}><option value="">Select</option><option value="female">Female</option><option value="male">Male</option></select></Field>
              <Field label="Date of birth" error={errors.date_of_birth}><input type="date" className={inputBig} value={form.date_of_birth} max={new Date().toISOString().slice(0, 10)} onChange={(e) => set('date_of_birth', e.target.value)} /></Field>
            </div>
            <Field label="Class applying for" error={errors.class_applied_id}>
              <select className={inputBig} value={form.class_applied_id} onChange={(e) => set('class_applied_id', e.target.value)}><option value="">Select class</option>{classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
            </Field>
            <Field label="Previous school (optional)"><input className={inputBig} value={form.previous_school} onChange={(e) => set('previous_school', e.target.value)} placeholder="Sunrise Nursery" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Religion"><select className={inputBig} value={form.religion} onChange={(e) => set('religion', e.target.value)}><option value="">Select</option><option>Christianity</option><option>Islam</option><option>Traditional</option><option>Other</option></select></Field>
              <Field label="State of origin"><input className={inputBig} value={form.state_of_origin} onChange={(e) => set('state_of_origin', e.target.value)} /></Field>
              <Field label="Nationality"><input className={inputBig} value={form.nationality} onChange={(e) => set('nationality', e.target.value)} /></Field>
              <Field label="Blood group (optional)"><select className={inputBig} value={form.blood_group} onChange={(e) => set('blood_group', e.target.value)}><option value="">Not sure</option>{['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((b) => <option key={b}>{b}</option>)}</select></Field>
            </div>
            <Field label="Allergies or medical conditions (optional)"><textarea rows={2} className={inputBig} value={form.medical_notes} onChange={(e) => set('medical_notes', e.target.value)} /></Field>
            <p className="text-xs text-slate-500">Each section saves as you go.</p>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Parent or guardian</p>
            <Field label="Full name" error={errors.guardian_name}><input className={inputBig} value={form.guardian_name} onChange={(e) => set('guardian_name', e.target.value)} placeholder="Mr Obi" /></Field>
            <Field label="Relationship"><select className={inputBig} value={form.guardian_relationship} onChange={(e) => set('guardian_relationship', e.target.value)}><option value="">Select</option><option value="father">Father</option><option value="mother">Mother</option><option value="guardian">Guardian</option><option value="other">Other</option></select></Field>
            <Field label="Phone number" error={errors.guardian_phone} hint="We send updates and the admission letter to this number."><input type="tel" inputMode="tel" className={inputBig} value={form.guardian_phone} onChange={(e) => set('guardian_phone', e.target.value)} placeholder="0803 111 2222" /></Field>
            <Field label="Email (optional)"><input type="email" className={inputBig} value={form.guardian_email} onChange={(e) => set('guardian_email', e.target.value)} placeholder="obi@example.com" /></Field>
            <Field label="Home address" error={errors.address}><input className={inputBig} value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="5 Adeola Street, Ikeja" /></Field>
          </div>
        )}

        {step === 2 && <DocsStep app={app} required={required} onChange={refresh} />}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Check everything</p>
            <Panel><Summary form={form} classes={classes} app={app} /></Panel>
            {cycle && cycle.form_fee > 0 && (
              <Panel><Rows rows={[['Application fee', naira(cycle.form_fee)]]} /><p className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100">Pay now or later. The entrance exam is booked after payment.</p></Panel>
            )}
            {!docsDone && <Panel tone="amber"><p className="text-sm text-amber-800">Some required documents are missing. Go back to upload them before submitting.</p></Panel>}
          </div>
        )}

        <Actions>
          {step < 3 ? (
            <Primary onClick={next} busy={save.isPending}>Next</Primary>
          ) : cycle && cycle.form_fee > 0 ? (
            <>
              <Primary onClick={() => submit.mutate()} busy={submit.isPending} disabled={!docsDone}>Submit and pay {naira(cycle.form_fee)}</Primary>
              <Secondary disabled={!docsDone || submit.isPending} onClick={() => submit.mutateAsync().then(() => nav('/applicant/overview')).catch(() => {})}>Submit and pay later</Secondary>
            </>
          ) : (
            <Primary onClick={() => submit.mutate()} busy={submit.isPending} disabled={!docsDone}>Submit application</Primary>
          )}
          {step > 0 && <Secondary onClick={() => { setStep(step - 1); setMsg(null); window.scrollTo({ top: 0 }); }}>Back</Secondary>}
        </Actions>
      </Body>
    </Screen>
  );
}

function Summary({ form, classes, app }: { form: Form; classes: { id: number; name: string }[]; app: Application | null }) {
  const req = app?.required_documents ?? [];
  return (
    <Rows rows={[
      ['Applicant', [form.first_name, form.middle_name, form.last_name].filter(Boolean).join(' ') || '—'],
      ['Class', classes.find((c) => String(c.id) === form.class_applied_id)?.name ?? app?.class_applied?.name ?? '—'],
      ['Date of birth', form.date_of_birth || '—'],
      ['Parent', form.guardian_name ? `${form.guardian_name}${form.guardian_phone ? ` · ${form.guardian_phone}` : ''}` : '—'],
      ['Documents', `${app?.documents.length ?? 0} of ${req.length || app?.documents.length || 0}`],
    ]} />
  );
}

function DocsStep({ app, required, onChange }: { app: Application | null; required: { type: string; label: string; required: boolean }[]; onChange: () => void }) {
  const picker = useRef<HTMLInputElement>(null);
  const [type, setType] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const up = useMutation({ mutationFn: (f: File) => postDocumentFile(type, f), onSuccess: () => { setErr(null); onChange(); }, onError: (e) => setErr(errMsg(e)) });
  const pick = (t: string) => { setType(t); picker.current?.click(); };
  const onFile = (f?: File) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setErr('Each file must be 5 MB or smaller.'); return; }
    up.mutate(f);
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Upload clear photos or PDFs</p>
      <input ref={picker} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
      {required.map((r) => {
        const d = app?.documents.find((x) => x.type === r.type);
        const busy = up.isPending && type === r.type;
        return (
          <Panel key={r.type}>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-lg font-bold text-[#0A2540]">{r.label || docLabel(r.type)}</p>
                <p className="text-sm text-slate-500">{d ? (d.status === 'rejected' ? 'Rejected. Upload a clearer copy' : 'Uploaded') : r.required ? 'Required' : 'Optional'}</p>
              </div>
              {d && d.status !== 'rejected'
                ? <button onClick={() => pick(r.type)} className="px-3 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-bold inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Uploaded</button>
                : <button onClick={() => pick(r.type)} disabled={busy} className="px-4 py-1.5 rounded-full bg-blue-100 text-blue-800 text-sm font-bold inline-flex items-center gap-1">{busy ? 'Uploading…' : <><Upload className="w-4 h-4" />Add</>}</button>}
            </div>
          </Panel>
        );
      })}
      {required.length === 0 && <Panel><p className="text-sm text-slate-600 flex items-center gap-2"><FileText className="w-4 h-4" />The school has not asked for any documents.</p></Panel>}
      {err && <Panel tone="red"><p className="text-sm text-red-700">{err}</p></Panel>}
      <p className="text-xs text-slate-500">Tap Add to take a photo or pick a file. Max 5 MB each.</p>
    </div>
  );
}
